/* MsalBootstrap — wraps the app in MsalProvider when MSAL_ENABLED is
   true, or renders children directly when Daedalus is in walkthrough /
   mock mode (VITE_USE_MOCKS=true) and MSAL env vars aren't set.

   This lets the same App tree serve both modes without branching in
   every scene. Individual scenes call useAuth() (below) to get the
   current user and to trigger loginRedirect / logoutRedirect. */

import {
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  PublicClientApplication,
  EventType,
  InteractionRequiredAuthError,
  type AccountInfo,
  type AuthenticationResult,
} from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import {
  MSAL_ENABLED,
  loginRequest,
  apiTokenRequest,
  graphFilesScope,
  graphSilentScopes,
  graphUserScope,
  storageScope,
} from './msalConfig';
import { setGraphTokenProvider } from '@/api/client';
import { MOCK_USER } from '@/api/mocks/fixtures';

/* ---------------- Auth context ---------------- */

export interface DaedalusUser {
  oid: string;
  name: string;
  username: string;
}

export interface AuthContextValue {
  user: DaedalusUser | null;
  ready: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  /** Returns a bearer access token for the APIM main audience. */
  getAccessToken: () => Promise<string | null>;
  /** The same token acquired SILENTLY: never redirects, null on any
   *  failure. For work the person did not just ask for (a chat's recovery
   *  poll, which can outlive the chat): a redirect there would navigate the
   *  page away from under them. Optional so stubbed test contexts needn't
   *  provide it. */
  getAccessTokenSilent?: () => Promise<string | null>;
  /** Returns a delegated Graph access token in the user's name with the
   *  Files.ReadWrite scope. Used to mint Office for the Web embed URLs
   *  whose WOPI session is bound to the real user identity (vs. our
   *  Graph SP), so the iframe lets the user actually type into cells.
   *  Silent first, then a popup, so call it from a click. Returns null if
   *  MSAL isn't ready or the sign-in didn't finish; the artifact panel's
   *  "Sign in and open" card says so. */
  getGraphAccessToken: () => Promise<string | null>;
  /** Returns a delegated Graph token (Sites.Read.All + Files.ReadWrite.All)
   *  acquired SILENTLY — no popup, returns null on any failure. Both scopes
   *  are admin-consented tenant-wide. Used for SharePoint security trimming:
   *  preflight site probes and delegated Office embeds. Optional so test
   *  harnesses that stub the context don't need to provide it. */
  getGraphTokenSilent?: () => Promise<string | null>;
  /** Interactive Graph sign-in for the artifact panel's "Sign in and open"
   *  button (Files.ReadWrite.All). Opens the MSAL popup straight from the
   *  click: MSAL opens the window before any await, so it stays inside the
   *  click's popup allowance. No silent attempt first: the panel only shows
   *  the button after the silent token failed, and a slow silent failure
   *  would outlast that allowance. Null if the popup was blocked, closed or
   *  failed. Optional so stubbed test contexts needn't provide it. */
  graphSignIn?: () => Promise<string | null>;
  /** Returns a delegated Azure Storage token (user_impersonation) for the
   *  401(k) workbook download — silent first, popup only if consent or a
   *  refresh is needed. Null on failure. Optional so stubbed test contexts
   *  needn't provide it. */
  getStorageToken?: () => Promise<string | null>;
  /** Resolves the signed-in user's org job title from Microsoft Graph
   *  (/me?$select=jobTitle). Best-effort: returns null on any failure or
   *  when the directory record has no title set, in which case the UI hides
   *  the title line. Optional so stubbed test contexts needn't provide it. */
  getJobTitle?: () => Promise<string | null>;
  /** True when VITE_USE_MOCKS=true; scenes use this to skip real auth. */
  mocked: boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() called outside <MsalBootstrap>');
  return ctx;
}

function accountToUser(a: AccountInfo): DaedalusUser {
  return {
    oid: (a.idTokenClaims?.oid as string) ?? a.localAccountId,
    name: a.name ?? a.username,
    username: a.username,
  };
}

/* ---------------- Real (MSAL) bootstrap ---------------- */

/* One ssoSilent attempt per page load, shared as a promise so React
   StrictMode's double-mounted effect doesn't fire two hidden-iframe
   round-trips (and so the second mount still receives the result the
   first mount kicked off). Resolves null on any failure — no Entra
   session in this browser profile, third-party-cookie blocking, etc. —
   and the caller falls through to the sign-in gate. */
let ssoSilentAttempt: Promise<AuthenticationResult | null> | null = null;
function attemptSsoSilent(
  msal: PublicClientApplication
): Promise<AuthenticationResult | null> {
  if (!ssoSilentAttempt) {
    // Scopes only — ssoSilent must NOT inherit loginRequest's
    // `prompt: 'select_account'` (it runs prompt=none in an iframe).
    ssoSilentAttempt = msal
      .ssoSilent({ scopes: loginRequest.scopes })
      .catch(() => null);
  }
  return ssoSilentAttempt;
}

function RealBootstrap({
  msal,
  children,
}: {
  msal: PublicClientApplication;
  children: ReactNode;
}) {
  const [ready, setReady] = useState(false);
  const [user, setUser] = useState<DaedalusUser | null>(null);

  // MSAL is already initialized + the redirect promise already handled in
  // main.tsx (before React mounts, so the URL fragment isn't stripped by
  // React Router). Here we just read out whatever account MSAL settled on
  // and wire the event callback for subsequent sign-ins / sign-outs.
  useEffect(() => {
    let cancelled = false;
    const active = msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
    if (active) {
      msal.setActiveAccount(active);
      setUser(accountToUser(active));
      setReady(true);
    } else {
      // No cached account in this tab. The MSAL cache is sessionStorage
      // (per-tab by design, so shared machines don't leak sessions across
      // browser profiles/users), which means every NEW tab boots signed
      // out even when the user is signed in one tab over. Before sending
      // them to the gate, try to adopt the browser's live Entra session
      // silently (hidden iframe, prompt=none). Success → same user, no
      // clicks; failure → ready flips and the RequireAuth guard routes
      // to /sign-in.
      void attemptSsoSilent(msal).then((result) => {
        if (cancelled) return;
        if (result?.account) {
          msal.setActiveAccount(result.account);
          setUser(accountToUser(result.account));
        }
        setReady(true);
      });
    }

    const callbackId = msal.addEventCallback((ev) => {
      if (ev.eventType === EventType.LOGIN_SUCCESS && 'account' in (ev.payload ?? {})) {
        const payload = ev.payload as AuthenticationResult;
        if (payload.account) {
          msal.setActiveAccount(payload.account);
          setUser(accountToUser(payload.account));
        }
      }
      if (ev.eventType === EventType.LOGOUT_SUCCESS) {
        setUser(null);
      }
    });

    return () => {
      cancelled = true;
      if (callbackId) msal.removeEventCallback(callbackId);
    };
  }, [msal]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      ready,
      mocked: false,
      login: async () => {
        await msal.loginRedirect(loginRequest);
      },
      logout: async () => {
        // Pass the account + logoutHint so Entra ends THIS user's session
        // directly instead of showing the "which account do you want to
        // sign out of?" picker. An abandoned picker leaves the SSO cookie
        // alive — which is how the next person's sign-in click silently
        // resumed the previous user's session on shared machines.
        const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
        const loginHint = (
          account?.idTokenClaims as { login_hint?: string } | undefined
        )?.login_hint;
        await msal.logoutRedirect({
          account: account ?? undefined,
          logoutHint: loginHint ?? account?.username,
        });
      },
      getAccessToken: async () => {
        const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
        if (!account) return null;
        try {
          const result = await msal.acquireTokenSilent({ ...apiTokenRequest, account });
          return result.accessToken;
        } catch (e) {
          // Redirect ONLY when MSAL says interaction is genuinely required
          // (expired refresh token, revoked consent, conditional access).
          // Everything else — a network blip, an Entra 5xx/throttle, an
          // interaction already in progress in another component — is
          // transient. The old bare catch redirected on ALL of these, which
          // navigated the whole SPA to Entra mid-dashboard-load whenever one
          // of N concurrent mounts hit a transient silent-refresh failure
          // (the "screen went blank, fine when I came back" reports).
          if (e instanceof InteractionRequiredAuthError) {
            await msal.acquireTokenRedirect(apiTokenRequest);
            return null;
          }
          // Transient: one short-delay retry (MSAL coalesces concurrent
          // silent requests, so parallel callers share this), then give up
          // with null — callers surface a per-tile error with a retry
          // affordance instead of the app navigating away.
          await new Promise((r) => setTimeout(r, 400));
          try {
            const result = await msal.acquireTokenSilent({ ...apiTokenRequest, account });
            return result.accessToken;
          } catch {
            return null;
          }
        }
      },
      getAccessTokenSilent: async () => {
        const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
        if (!account) return null;
        try {
          const result = await msal.acquireTokenSilent({ ...apiTokenRequest, account });
          return result.accessToken;
        } catch {
          return null;
        }
      },
      getGraphAccessToken: async () => {
        const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
        if (!account) return null;
        try {
          const result = await msal.acquireTokenSilent({ ...graphFilesScope, account });
          return result.accessToken;
        } catch {
          // Silent failed — incremental consent or refresh needed.
          // Use popup (not redirect) so the chat surface doesn't
          // navigate away mid-action. The user sees one consent
          // prompt the first time they open a spreadsheet; subsequent
          // opens are silent.
          try {
            const result = await msal.acquireTokenPopup(graphFilesScope);
            return result.accessToken;
          } catch {
            return null;
          }
        }
      },
      graphSignIn: async () => {
        const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
        try {
          const result = await msal.acquireTokenPopup({
            ...graphFilesScope,
            account: account ?? undefined,
          });
          return result.accessToken;
        } catch {
          return null;
        }
      },
      getStorageToken: async () => {
        const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
        if (!account) return null;
        try {
          const result = await msal.acquireTokenSilent({ ...storageScope, account });
          return result.accessToken;
        } catch (e) {
          // Popup, not redirect: the user is on the 401(k) tab mid-download.
          if (!(e instanceof InteractionRequiredAuthError)) return null;
          try {
            const result = await msal.acquireTokenPopup(storageScope);
            return result.accessToken;
          } catch {
            return null;
          }
        }
      },
      getGraphTokenSilent: async () => {
        const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
        if (!account) return null;
        try {
          // Silent only — both scopes are admin-consented, so this succeeds
          // without a prompt. Never falls back to a popup: this runs on the
          // preflight hot path and inside the embed load effect, where an
          // interactive prompt would be disruptive or break the load.
          const result = await msal.acquireTokenSilent({ ...graphSilentScopes, account });
          return result.accessToken;
        } catch {
          return null;
        }
      },
      getJobTitle: async () => {
        const account = msal.getActiveAccount() ?? msal.getAllAccounts()[0];
        if (!account) return null;
        try {
          // Silent only — User.Read is a default-consented delegated scope,
          // so this resolves without a prompt. Best-effort: any failure (or
          // an empty directory title) returns null and the UI hides the line.
          const result = await msal.acquireTokenSilent({ ...graphUserScope, account });
          const res = await fetch('https://graph.microsoft.com/v1.0/me?$select=jobTitle', {
            headers: { Authorization: `Bearer ${result.accessToken}` },
          });
          if (!res.ok) return null;
          const data = (await res.json()) as { jobTitle?: string | null };
          const title = (data.jobTitle ?? '').trim();
          return title.length > 0 ? title : null;
        } catch {
          return null;
        }
      },
    }),
    [msal, user, ready]
  );

  // Register the silent Graph-token getter so client.preflightAuth can attach
  // it as X-Graph-Token without threading it through every chat call site.
  useEffect(() => {
    setGraphTokenProvider(value.getGraphTokenSilent ?? null);
    return () => setGraphTokenProvider(null);
  }, [value.getGraphTokenSilent]);

  return (
    <AuthContext.Provider value={value}>
      <MsalProvider instance={msal}>{children}</MsalProvider>
    </AuthContext.Provider>
  );
}

/* ---------------- Mocked bootstrap (Phase 0.5) ---------------- */

function MockBootstrap({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<DaedalusUser | null>(null);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      ready: true,
      mocked: true,
      login: async () => {
        setUser({ oid: MOCK_USER.oid, name: MOCK_USER.name, username: MOCK_USER.email });
      },
      logout: async () => {
        setUser(null);
      },
      getAccessToken: async () => 'mock-token',
      getAccessTokenSilent: async () => 'mock-token',
      getGraphAccessToken: async () => 'mock-graph-token',
      getGraphTokenSilent: async () => null,
      graphSignIn: async () => null,
      getStorageToken: async () => null,
      getJobTitle: async () => MOCK_USER.role,
    }),
    [user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/* ---------------- Public component ----------------

   `msal` is the already-initialized PublicClientApplication from main.tsx
   (null when MSAL_ENABLED is false). Passing it in as a prop — rather
   than constructing here — guarantees MSAL's handleRedirectPromise has
   already consumed the `#code=...` fragment by the time React mounts.
*/

export function MsalBootstrap({
  msal,
  children,
}: {
  msal: PublicClientApplication | null;
  children: ReactNode;
}) {
  if (MSAL_ENABLED && msal) {
    return <RealBootstrap msal={msal}>{children}</RealBootstrap>;
  }
  return <MockBootstrap>{children}</MockBootstrap>;
}
